/**
 * RoiCalculatorDialog — the calculator surface, open to every signed-in user.
 *
 * T-627: this surface used to refuse to render for a non-admin, as defense in depth behind the
 * entry control's own role gate. Both gates are gone now that the calculator ships to all users;
 * the session gate (RequireAuth around the map) is the only access boundary left, and there is no
 * server-side counterpart to relax because nothing here calls a privileged endpoint.
 *
 * State: a charger-type toggle seeds the inputs from ROI_PRESETS[type]; each edit updates one field;
 * results are recomputed live by the shared engine. No ROI math is re-implemented here — every output
 * is a computeRoi(inputs). Prefill from site context is deferred (the ZIP→utility-rate lookup that
 * would populate the rate is not built yet), so inputs start from the preset defaults; the launch is
 * contextual by placement (the selected site's score panel), not data-populated.
 */
import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog'
import { computeRoi, type RoiInputs } from '../../lib/roi/roiModel'
import { ROI_PRESETS, type ChargerType } from '../../lib/roi/presets'
import { RoiInputsForm } from './RoiInputsForm'
import { RoiResultsView } from './RoiResultsView'

export interface RoiCalculatorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** T-478: the selected site's address (searched or map-clicked), shown as context so the model
   *  is anchored to a place. The ROI inputs are charger economics with no address field, so this is
   *  contextual only — not a computed input. */
  siteAddress?: string | null
  /** T-478: true when `siteAddress` is a map-click reverse-geocoded (approximate) address. */
  addressApproximate?: boolean
}

const CHARGERS: { type: ChargerType; label: string }[] = [
  { type: 'dcfc', label: 'DC fast (DCFC)' },
  { type: 'l2', label: 'Level 2 (L2)' },
]

export function RoiCalculatorDialog({
  open,
  onOpenChange,
  siteAddress = null,
  addressApproximate = false,
}: RoiCalculatorDialogProps) {
  const [chargerType, setChargerType] = useState<ChargerType>('dcfc')
  const [inputs, setInputs] = useState<RoiInputs>(ROI_PRESETS.dcfc)
  const results = useMemo(() => computeRoi(inputs), [inputs])

  function selectCharger(type: ChargerType) {
    setChargerType(type)
    setInputs(ROI_PRESETS[type]) // toggling loads that tab's ship defaults
  }
  function updateField(key: keyof RoiInputs, value: number) {
    setInputs((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ROI calculator</DialogTitle>
          <DialogDescription>
            Site-level charging economics from Noreen's workbook model. Start from a charger preset
            and edit any assumption — results update live.
          </DialogDescription>
          {/* T-478: anchor the model to the selected site. The inputs are charger economics (no
              address field), so this is context, not a computed input — with the approximate note
              carried through from a map-clicked point. */}
          {siteAddress && (
            <p data-testid="roi-site-address" className="text-xs text-muted-foreground">
              <span className="font-medium text-secondary-foreground">{siteAddress}</span>
              {addressApproximate && ' · approximate, based on map location'}
            </p>
          )}
        </DialogHeader>

        {/* Charger-type toggle — loads the matching preset. */}
        <div
          role="group"
          aria-label="Charger type"
          className="inline-flex rounded-lg border border-border p-0.5"
        >
          {CHARGERS.map((c) => {
            const active = chargerType === c.type
            return (
              <button
                key={c.type}
                type="button"
                aria-pressed={active}
                onClick={() => selectCharger(c.type)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {c.label}
              </button>
            )
          })}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <RoiInputsForm inputs={inputs} chargerType={chargerType} onChange={updateField} />
          <RoiResultsView results={results} chargerType={chargerType} />
        </div>

        <p className="mt-1 text-[0.66rem] leading-snug text-muted-foreground">
          Directional estimate, not investment advice. Federal 30C credit expired 2026-06-30 — enter
          only state / utility / LCFS incentives. Ranges shown are 2025–2026 US market benchmarks.
        </p>
      </DialogContent>
    </Dialog>
  )
}
